import useFavoritesService from 'api/services/favoritesService'
import { getUserId } from 'api/utils/AuthManager'
import {
  consumeTempFavorites,
  isTempFavorite,
  toggleTempFavorite,
} from 'api/utils/tempFavorites'
import { createElement, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { ListedProperty } from 'types/property'

type UseListingFavoriteOptions = {
  properties?: ListedProperty[]
  onToggleFavorite?: (propertyId: string, isFavorited: boolean) => void
}

const useListingFavorite = ({
  properties,
  onToggleFavorite,
}: UseListingFavoriteOptions = {}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { addFavorite } = useFavoritesService()
  const { mutateAsync: favoriteMutation } = useMutation(addFavorite)
  const [loadingFavoriteId, setLoadingFavoriteId] = useState<string | null>(null)
  const [favoriteEpoch, setFavoriteEpoch] = useState(0)
  const inFlightRef = useRef<Set<string>>(new Set())
  const syncedTempRef = useRef(false)

  const applyLocalFavorite = useCallback(
    (propertyId: string, isFavorited: boolean) => {
      const property = properties?.find(item => String(item.id) === String(propertyId))
      if (property) {
        property.isFavorited = isFavorited
      }
      onToggleFavorite?.(propertyId, isFavorited)
      setFavoriteEpoch(value => value + 1)
    },
    [properties, onToggleFavorite]
  )

  useEffect(() => {
    if (getUserId() || !properties?.length) return
    for (const property of properties) {
      if (isTempFavorite(property.id) && !property.isFavorited) {
        property.isFavorited = true
      }
    }
  }, [properties])

  useEffect(() => {
    const userId = getUserId()
    if (!userId || syncedTempRef.current) return
    const pending = consumeTempFavorites()
    if (pending.length === 0) {
      syncedTempRef.current = true
      return
    }
    syncedTempRef.current = true
    void (async () => {
      for (const id of pending) {
        try {
          await favoriteMutation(id)
          applyLocalFavorite(id, true)
        } catch {
          // continue
        }
      }
    })()
  }, [favoriteMutation, applyLocalFavorite])

  const goLoginToSave = (propertyId: string) => {
    navigate('/auth', {
      state: {
        favorite: propertyId,
        from: location,
      },
    })
  }

  const handleFavorite = async (propertyId: string) => {
    if (inFlightRef.current.has(propertyId) || loadingFavoriteId === propertyId) {
      return
    }

    const userId = getUserId()
    const property = properties?.find(item => String(item.id) === String(propertyId))
    const wasFavorited = Boolean(property?.isFavorited || isTempFavorite(propertyId))

    if (!userId) {
      const nowFavorited = toggleTempFavorite(propertyId)
      applyLocalFavorite(propertyId, nowFavorited)

      if (nowFavorited) {
        toast.info(
          createElement(
            'div',
            { className: 'text-sm' },
            createElement('p', null, String(t('propertyCard.tempFavoriteSaved'))),
            createElement(
              'button',
              {
                type: 'button',
                className: 'mt-2 font-semibold text-imovPrimary underline',
                onClick: () => goLoginToSave(propertyId),
              },
              String(t('propertyCard.loginToKeepFavorite'))
            )
          ),
          { autoClose: 6000 }
        )
      } else {
        toast.success(String(t('propertyCard.favoriteRemoved')))
      }
      return
    }

    inFlightRef.current.add(propertyId)
    setLoadingFavoriteId(propertyId)
    applyLocalFavorite(propertyId, !wasFavorited)

    try {
      await favoriteMutation(propertyId)
      toast.success(
        String(
          !wasFavorited
            ? t('propertyCard.favoriteAdded')
            : t('propertyCard.favoriteRemoved')
        )
      )
    } catch {
      applyLocalFavorite(propertyId, wasFavorited)
      toast.error(String(t('propertyCard.favoriteFailed')))
    } finally {
      inFlightRef.current.delete(propertyId)
      setLoadingFavoriteId(null)
    }
  }

  return { handleFavorite, loadingFavoriteId, favoriteEpoch, goLoginToSave }
}

export default useListingFavorite

